import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import useDebouncedCallback from 'use-debounce/lib/useDebouncedCallback';

import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { billPresentmentAPI } from 'app-components/flux/bill-presentment';
import type { Biller } from 'app-components/flux/bill-presentment/billPresentmentTypes';
import { extractHostname } from 'app-components/flux/bill-presentment/billPresentmentUtils';
import { getLogger, tracker } from 'app-components/utils/core';

import { useBillersWithLogos } from 'app-components/flux/bill-presentment/billPresentmentHooks';
import { PopularBillersGrid } from './PopularBillersGrid';

const log = getLogger('BillPresentment/SearchView');

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    flexBasis: 504,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    padding: theme.spacing(4),
    boxSizing: 'border-box',
    alignItems: 'center', // ensure children are centered
    overflowX: 'hidden',
  },
  logoImage: {
    border: `1px solid ${theme.palette.border.light}`,
    width: 50,
    objectFit: 'contain',
    borderRadius: 8,
    marginRight: theme.spacing(1),
  },
  dropdownFooter: {
    borderTop: `1px solid ${theme.palette.border.light}`,
    borderColor: 'divider',
    padding: theme.spacing(2),
  },
  grid: {
    width: '100%',
    zIndex: 0,
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
  },
  autocompleteInputRoot: {
    boxSizing: 'border-box',
    width: '100%',
    marginBottom: theme.spacing(5),
    '& .MuiFilledInput-root': {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1),
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingRight: '8px !important', // override default right padding
    },
    '& .MuiAutocomplete-endAdornment': {
      display: 'none',
      width: 0,
      padding: 0,
      margin: 0,
    },
  },
}));

interface SearchViewProps {
  handleSelect: (biller: Biller) => void;
}

const SearchView = ({ handleSelect }: SearchViewProps) => {
  const { classes } = useStyles();
  const [hasSearched, setHasSearched] = useState(false);

  const [billers, setBillers] = useState<Biller[]>([]);
  const billersWithLogos = useBillersWithLogos(billers);

  const [debouncedSearch] = useDebouncedCallback(
    async (searchVal: string) => {
      setHasSearched(true);
      try {
        const response = await billPresentmentAPI.search(searchVal);
        setBillers(response.data);
        tracker.track(tracker.events.billerSearch, { searchQuery: searchVal, resultsFound: response?.data?.length });
      } catch (e) {
        log.warn('error in search!', e);
        setBillers([]);
      }
    },
    300,
    { leading: true, trailing: true },
  );

  return (
    <div className={classes.root}>
      <Autocomplete<Biller, false, true, false>
        disableClearable
        clearIcon={null}
        onChange={(_e, value, _reason, _details) => {
          if (value) handleSelect(value);
        }}
        onInputChange={(_e, value, _details) => {
          if (value && value.length > 2) {
            debouncedSearch(value);
          } else {
            setBillers([]);
            setHasSearched(false);
          }
        }}
        popupIcon={null}
        className={classes.autocompleteInputRoot}
        options={billers}
        noOptionsText={hasSearched ? 'No billers found' : 'Start typing to search for billers'}
        getOptionLabel={(option) => option.name}
        filterSelectedOptions
        renderOption={(props, option, { selected }) => {
          const url = extractHostname(option) || '';
          const classification = option?.classification?.trim() || '';
          let secondary = '';
          if (url && classification) {
            secondary = `${url} · ${classification}`;
          } else if (url) {
            secondary = url;
          } else if (classification) {
            secondary = classification;
          }
          const logo = billersWithLogos.find((biller: Biller) => biller.id === option.id)?.logoKey;
          return (
            <MenuItem {...props} key={option.id} selected={selected} onClick={() => handleSelect(option)}>
              {logo && <img src={logo} alt={option.name} className={classes.logoImage} />}
              <ListItemText primary={option.name} secondary={secondary} />
            </MenuItem>
          );
        }}
        renderInput={(params) => (
          <TextField
            sx={{
              '& .MuiInputLabel-shrink': {
                transform: 'translate(12px, 2px) scale(0.75)',
              },
            }}
            {...params}
            variant='filled'
            id='search-biller'
            label='Search for your biller'
            autoComplete='one-time-code'
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton aria-label='Search' onClick={() => setBillers([])} size='large'>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: { position: 'relative' },
          },
        }}
        slots={{
          // override the Paper component of the dropdown
          paper: (props) => (
            <Paper {...props}>
              {props.children}
              {hasSearched && (
                <Box className={classes.dropdownFooter}>
                  <Typography variant='paragraphRegular' color='text.secondary'>
                    Can’t find your biller? We may not support it yet.
                  </Typography>
                </Box>
              )}
            </Paper>
          ),
        }}
        filterOptions={(x) => x} // need this to disable default filter and use QCS matching
      />

      {/* If we receive an endpoint for popular billers, they should come here */}
      <div className={classes.grid}>
        <PopularBillersGrid handleSelect={handleSelect} />
      </div>
    </div>
  );
};

export default SearchView;
